/**
 * IndexedDB, via `idb`. Four stores:
 *
 *   completions  keyed by stopUuid   the completion record (state machine)
 *   photos       keyed by key        Blob per (stop, item_type)
 *   route        keyed by 'active'   the last route the server handed us, so
 *                                    the app opens with a route offline
 *   meta         keyed by name       tokens, flags (persist requested, ...)
 *
 * Every write is awaited before the caller reports success to the driver.
 * IndexedDB is the record; the server is a copy that catches up.
 */
import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { CompletionRecord, FindingRecord, PhotoRecord, QueueSummary, SyncState } from './types'

export const DB_NAME = 'rbf-deliveries'
// v3 (2026-09-14): the `findings` store - notes and reference photos a driver adds.
export const DB_VERSION = 3

interface DeliveriesDB extends DBSchema {
  completions: {
    key: string
    value: CompletionRecord
    indexes: { 'by-state': SyncState; 'by-route': number }
  }
  photos: {
    key: string
    value: PhotoRecord
    indexes: { 'by-state': SyncState; 'by-stop': string }
  }
  route: {
    key: string
    value: { key: string; savedAt: number; route: unknown }
  }
  meta: {
    key: string
    value: { key: string; value: unknown }
  }
  /** v2: a stop's access facts, prefetched at route load so they read offline. */
  access: {
    key: string
    value: { stopUuid: string; fetchedAt: number; access: unknown }
  }
  /** v2: reference-photo Blobs for the above. */
  assets: {
    key: string
    value: { key: string; stopUuid: string; blob: Blob; contentType: string }
    indexes: { 'by-stop': string }
  }
  /** v3: what a driver found at a property - notes and reference photos. */
  findings: {
    key: string
    value: FindingRecord
    indexes: { 'by-stop': string; 'by-state': SyncState }
  }
}

let dbPromise: Promise<IDBPDatabase<DeliveriesDB>> | null = null

export function openDb(): Promise<IDBPDatabase<DeliveriesDB>> {
  if (!dbPromise) {
    dbPromise = openDB<DeliveriesDB>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          const completions = db.createObjectStore('completions', { keyPath: 'stopUuid' })
          completions.createIndex('by-state', 'state')
          completions.createIndex('by-route', 'routeId')
          const photos = db.createObjectStore('photos', { keyPath: 'key' })
          photos.createIndex('by-state', 'state')
          photos.createIndex('by-stop', 'stopUuid')
          db.createObjectStore('route', { keyPath: 'key' })
          db.createObjectStore('meta', { keyPath: 'key' })
        }
        if (oldVersion < 2) {
          db.createObjectStore('access', { keyPath: 'stopUuid' })
          const assets = db.createObjectStore('assets', { keyPath: 'key' })
          assets.createIndex('by-stop', 'stopUuid')
        }
        if (oldVersion < 3) {
          const findings = db.createObjectStore('findings', { keyPath: 'id' })
          findings.createIndex('by-stop', 'stopUuid')
          findings.createIndex('by-state', 'state')
        }
      },
    })
  }
  return dbPromise
}

/** Tests only: drop the cached connection so a fresh fake DB is opened. */
export function _resetDbForTests(): void {
  dbPromise = null
}

// ── completions ──────────────────────────────────────────────────────────────

export async function putCompletion(rec: CompletionRecord): Promise<void> {
  const db = await openDb()
  await db.put('completions', rec)
}

export async function getCompletion(stopUuid: string): Promise<CompletionRecord | undefined> {
  const db = await openDb()
  return db.get('completions', stopUuid)
}

export async function listCompletions(): Promise<CompletionRecord[]> {
  const db = await openDb()
  return db.getAll('completions')
}

// ── photos ───────────────────────────────────────────────────────────────────

export function photoKey(stopUuid: string, itemType: string): string {
  return `${stopUuid}:${itemType}`
}

export async function putPhoto(rec: PhotoRecord): Promise<void> {
  const db = await openDb()
  await db.put('photos', rec)
}

export async function getPhoto(key: string): Promise<PhotoRecord | undefined> {
  const db = await openDb()
  return db.get('photos', key)
}

export async function listPhotos(): Promise<PhotoRecord[]> {
  const db = await openDb()
  return db.getAll('photos')
}

export async function listPhotosForStop(stopUuid: string): Promise<PhotoRecord[]> {
  const db = await openDb()
  return db.getAllFromIndex('photos', 'by-stop', stopUuid)
}

// ── route cache ──────────────────────────────────────────────────────────────

export async function saveRoute(route: unknown): Promise<void> {
  const db = await openDb()
  await db.put('route', { key: 'active', savedAt: Date.now(), route })
}

export async function loadRoute<T = unknown>(): Promise<{ savedAt: number; route: T } | undefined> {
  const db = await openDb()
  const row = await db.get('route', 'active')
  return row ? { savedAt: row.savedAt, route: row.route as T } : undefined
}

// ── meta ─────────────────────────────────────────────────────────────────────

export async function setMeta(key: string, value: unknown): Promise<void> {
  const db = await openDb()
  await db.put('meta', { key, value })
}

export async function getMeta<T = unknown>(key: string): Promise<T | undefined> {
  const db = await openDb()
  const row = await db.get('meta', key)
  return row ? (row.value as T) : undefined
}

// ── the number the driver sees ───────────────────────────────────────────────

export async function queueSummary(): Promise<QueueSummary> {
  const [completions, photos] = await Promise.all([listCompletions(), listPhotos()])
  const pendingStopSet = new Set<string>()
  let syncing = 0
  let failed = 0
  let synced = 0
  let lastError = ''
  let nextAttemptAt: number | null = null
  const note = (state: SyncState, stopUuid: string, err: string, next: number) => {
    // 'local' is a checklist still in the driver's hands - nothing to upload yet.
    if (state === 'synced' || state === 'local') return
    pendingStopSet.add(stopUuid)
    if (state === 'syncing') syncing += 1
    if (state === 'failed') {
      failed += 1
      if (err) lastError = err
      if (nextAttemptAt === null || next < nextAttemptAt) nextAttemptAt = next
    }
  }
  for (const c of completions) {
    if (c.state === 'synced') synced += 1
    note(c.state, c.stopUuid, c.lastError, c.nextAttemptAt)
  }
  let pendingPhotos = 0
  for (const p of photos) {
    if (p.state !== 'synced' && p.state !== 'local') pendingPhotos += 1
    note(p.state, p.stopUuid, p.lastError, p.nextAttemptAt)
  }
  // Findings (notes and reference photos a driver added) wait in the same queue and
  // count toward the same number: "0 pending" must never hide a note still on the phone.
  for (const f of await listFindings()) {
    if (f.kind === 'photo' && f.state !== 'synced' && f.state !== 'local') pendingPhotos += 1
    note(f.state, f.stopUuid, f.lastError, f.nextAttemptAt)
  }
  const lastDrainAt = (await getMeta<number>('lastDrainAt')) ?? null
  return {
    pendingStops: pendingStopSet.size,
    pendingPhotos,
    syncing,
    failed,
    synced,
    lastError,
    nextAttemptAt,
    lastDrainAt,
  }
}

// ── access facts + reference photos (prefetched, read offline) ───────────────

export async function putAccess(stopUuid: string, access: unknown): Promise<void> {
  const db = await openDb()
  await db.put('access', { stopUuid, fetchedAt: Date.now(), access })
}

export async function getAccess<T = unknown>(stopUuid: string): Promise<{ fetchedAt: number; access: T } | undefined> {
  const db = await openDb()
  const row = await db.get('access', stopUuid)
  return row ? { fetchedAt: row.fetchedAt, access: row.access as T } : undefined
}

export async function putAsset(key: string, stopUuid: string, blob: Blob, contentType: string): Promise<void> {
  const db = await openDb()
  await db.put('assets', { key, stopUuid, blob, contentType })
}

export async function getAsset(key: string): Promise<Blob | undefined> {
  const db = await openDb()
  const row = await db.get('assets', key)
  return row?.blob
}

export async function deletePhoto(key: string): Promise<void> {
  const db = await openDb()
  await db.delete('photos', key)
}

/** Only ever called for a SYNCED record the server has since superseded. */
export async function deleteCompletionAndPhotos(stopUuid: string): Promise<void> {
  const db = await openDb()
  await db.delete('completions', stopUuid)
  for (const p of await db.getAllFromIndex('photos', 'by-stop', stopUuid)) {
    if (p.state === 'synced') await db.delete('photos', p.key)
  }
}

// ── findings: what a driver found (sync/findings.ts) ─────────────────────────

export async function putFinding(rec: FindingRecord): Promise<void> {
  const db = await openDb()
  await db.put('findings', rec)
}

export async function listFindings(): Promise<FindingRecord[]> {
  const db = await openDb()
  return db.getAll('findings')
}

export async function listFindingsForStop(stopUuid: string): Promise<FindingRecord[]> {
  const db = await openDb()
  return db.getAllFromIndex('findings', 'by-stop', stopUuid)
}
